/**
 * Tastaturbedienung einer Auswahlliste — nur die Rechnung, ohne DOM.
 *
 * Die Liste im Anlagedialog ist bei zweihundert Projekten mit der Maus muehsam:
 * suchen, greifen, zielen. Mit den Pfeiltasten laeuft man vom Suchfeld direkt
 * durch die Treffer. Welcher Eintrag als naechstes den Fokus bekommt, ist eine
 * reine Rechnung — sie liegt deshalb hier und nicht im Dialog, wo sie nur mit
 * jsdom pruefbar waere (das Projekt hat keins).
 */

/** Die Tasten, die eine Liste bewegen. Alles andere geht die Liste nichts an. */
export type ListNavKey = 'ArrowDown' | 'ArrowUp' | 'Home' | 'End';

/** True, wenn diese Taste die Liste bewegt — spart dem Aufrufer die Aufzaehlung. */
export const isListNavKey = (key: string): key is ListNavKey =>
    key === 'ArrowDown' || key === 'ArrowUp' || key === 'Home' || key === 'End';

/**
 * Der Index, der nach dieser Taste den Fokus traegt.
 *
 * `current` ist `-1`, wenn der Fokus noch ausserhalb der Liste steht (im
 * Suchfeld). Von dort fuehrt Pfeil-ab auf den ersten Treffer.
 *
 * Der Rueckgabewert `-1` bedeutet dasselbe rueckwaerts: Pfeil-auf auf dem
 * ersten Eintrag gibt den Fokus ans Suchfeld zurueck. Ohne diesen Ausgang
 * waere die Liste eine Falle — man kaeme mit der Tastatur hinein, aber nicht
 * mehr zum Tippen zurueck.
 *
 * Bewusst OHNE Umlauf am Ende: die Liste ist nach Betrag sortiert und lang.
 * Ein Sprung vom letzten auf den ersten Eintrag saehe aus wie ein Fehlgriff,
 * und wer ans andere Ende will, hat Pos1/Ende.
 */
export const nextListIndex = (current: number, count: number, key: ListNavKey): number => {
    // Leere Liste: es gibt nichts zu fokussieren, der Fokus bleibt im Suchfeld.
    if (count <= 0) return -1;
    switch (key) {
        case 'ArrowDown':
            // Am unteren Ende stehenbleiben statt umzulaufen.
            return Math.min(Math.max(current, -1) + 1, count - 1);
        case 'ArrowUp':
            // Unter 0 nur bis -1: das ist der Rueckweg ins Suchfeld, nicht weiter.
            return Math.max(Math.min(current, count) - 1, -1);
        case 'Home':
            return 0;
        case 'End':
            return count - 1;
    }
};
